/**
 * 市民端「快速咨询」**开场白** · 前后端同一份契约真本（2026-09-19）。
 *
 * ════════ 这件事是怎么来的 ════════
 *
 * 主公 2026-09-19：「后台增加一个地方可以修改快速咨询的欢迎语，和推荐问题那个放在一个板块」。
 * 改前那段开场白写死在 `packages/citizen/src/pages/Consult.tsx` 的 `GREETING` 常量里 ——
 * 与建议问题（`quick-question-contract.ts`）改前的处境一模一样：协会想换一句话，
 * 得改代码、重新构建、重新发布。
 *
 * ════════ 与建议问题为什么是两条配置、而不是塞进同一条 ════════
 *
 * 界面上它们同处一屏（协会眼里是「快速咨询长什么样」这一件事），但**存储上必须分开**：
 *   · 建议问题那条 `app_setting` 已经有存量数据了，改它的形状要写迁移；
 *   · 两者的改动频率与改动人不同（开场白几乎不动，问题常换），
 *     分开存才能让 op_log 的改前改后一眼看出「这次动的是哪一样」。
 * 所以：**一屏两块、两个保存按钮、两条 `app_setting`、两条留痕**。
 * 一个按钮存两条配置的写法被否掉了 —— 它必然出现「前一条存进去了、后一条失败」
 * 的半成功，而那种状态在界面上说不清楚。
 *
 * ════════ 为什么这里允许换行，而建议问题不允许 ════════
 *
 * 建议问题是一行 chip，任何控制字符都只会把版式撑坏，所以那边一律拒绝。
 * 开场白是一段话，**现行默认值本身就是三行**（自我介绍 / 能问什么 / AI 免责），
 * 分行是它表意的一部分。所以这里：**换行允许，其余控制字符照旧拒绝**。
 */

/** 配置项的键。`app_setting.key` 存的就是它，全库唯一。 */
export const CONSULT_GREETING_SETTING_KEY = 'citizen.consult_greeting';

/**
 * 开场白字数上限（按**码点**数，与建议问题同一种数法）。
 *
 * 200 的由来：现行默认值 55 字，留三倍余量足够协会把话说完；
 * 再长就会把首屏的建议问题挤出可视区 —— 那几枚 chip 才是这一屏要人点的东西。
 */
export const MAX_CONSULT_GREETING_LEN = 200;

/** 开场白最多几行。三行是现状，给到 6 行；再多就不是开场白是公告了。 */
export const MAX_CONSULT_GREETING_LINES = 6;

/**
 * 兜底开场白 —— 与改前 `Consult.tsx` 的 `GREETING` 常量**逐字一致**。
 *
 * 两个用处（与建议问题那份兜底同构）：
 *   ① 后台配置页在「从没配过」时给出的初始内容；
 *   ② 市民端**拉不到配置时**的回落 —— 首屏那段话绝不能空着。
 *
 * ★ 这里用 `join` 拼而不是写成带反斜杠 n 的字面量：源码经过多层写入链路时，
 *   转义序列被吃掉过不止一次（中枢记忆「验收源码禁写字面控制字符」）。
 *   拼出来的换行是运行期产生的，写入链路碰不到它。
 */
export const DEFAULT_CONSULT_GREETING: string = [
  '您好，我是成都房经协小助手。',
  '入会、星级、工牌、政策、投诉等问题，都可以随时问我。',
  'AI生成内容仅供参考。',
].join('\n');

/** 后台读写用的整份配置。 */
export interface ConsultGreetingConfig {
  greeting: string;
  /** 最后一次保存时间（ISO）。从没配过时为 null */
  updatedAt: string | null;
  /** 最后一次保存人（staff_id）。从没配过时为 null */
  updatedBy: string | null;
  /** 当前这份配置存在哪：postgres / json。给排障看，不影响业务 */
  store?: string;
  /** true = 库里没有这条配置，返回的是 `DEFAULT_CONSULT_GREETING` 打底 */
  isDefault?: boolean;
}

/** 一个字符是不是**换行以外**的控制字符。 */
function isBannedControlChar(ch: string): boolean {
  const code = ch.codePointAt(0) ?? 0;
  const LF = 10;
  const CR = 13;
  if (code === LF || code === CR) return false; // 换行是开场白的正当排版手段
  return code < 0x20 || code === 0x7f;
}

/** 按码点数字数（`String.length` 会把 emoji 与生僻字算成两个）。 */
export function consultGreetingLength(text: string): number {
  return Array.from(text).length;
}

/**
 * 归一化：统一行尾为 LF、去首尾空白、把三个以上连续换行压成两个。
 *
 * ★ 与建议问题那边同一条纪律：**只整理排版，不替用户改内容**。
 *   中间的空格一个不动，字也一个不改。行尾统一是必须做的 ——
 *   Windows 上粘贴进来的 CRLF 存进库、再渲染到市民端会多出一堆空行。
 */
export function normalizeConsultGreeting(text: string): string {
  return String(text ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * 校验开场白。通过返回 null，不通过返回**中文原因**（原样给工作人员看）。
 *
 * ★ 请先 `normalizeConsultGreeting` 再校验，否则「首尾带空行但不超字数」会被放进去。
 * ★ **不允许为空**：空开场白在市民端就是一片空白，那不是「协会就是这么配的」，
 *   而是一个没人看得懂的故障态。要想去掉开场白，得先有人明确提这个需求。
 */
export function validateConsultGreeting(text: unknown): string | null {
  if (typeof text !== 'string') return '开场白必须是文本';
  if (text.trim() === '') return '开场白不能为空';
  if (text !== text.trim()) return '开场白首尾不能有空白';
  for (const ch of text) {
    if (isBannedControlChar(ch)) return '开场白不能包含换行以外的控制字符';
  }
  const n = consultGreetingLength(text);
  if (n > MAX_CONSULT_GREETING_LEN) {
    return `开场白不超过 ${MAX_CONSULT_GREETING_LEN} 个字，当前有 ${n} 个字`;
  }
  const lines = text.split('\n').length;
  if (lines > MAX_CONSULT_GREETING_LINES) {
    return `开场白最多 ${MAX_CONSULT_GREETING_LINES} 行，当前有 ${lines} 行`;
  }
  return null;
}

/**
 * 从存储里读出来的那坨 JSON 解析成开场白文本。形状不对返回 null，
 * **绝不猜**（调用方据此走「按默认值返回并打 error 日志」那条路）。
 */
export function parseConsultGreeting(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const raw = (value as Record<string, unknown>)['greeting'];
  if (typeof raw !== 'string') return null;
  const text = normalizeConsultGreeting(raw);
  return validateConsultGreeting(text) === null ? text : null;
}
